import type { BrushKind, FriendDTO, MonsterDTO, PersonalityId, SkillId } from '@shared/protocol/messages';
import type { App } from '../../app/App';
import { Scene } from '../core/Scene';
import type { ViewportInfo } from '../core/Viewport';
import { FighterView } from '../entities/FighterView';
import { acquireArt, type ArtHandle } from '../entities/art';
import { RULES, skillDef, tierDef } from '../combat/rules';
import { rewardCap, statPower, validateMonster } from '../combat/budget';
import { HERO_CLASSES, heroClass } from '../data/presets';
import { createHero } from '../systems/hero';
import { newId, type Monster } from '../systems/model';
import { addMemory, ownedPens } from '../systems/progression';
import { BRUSH_LOOKS, DrawingCanvas, EASY_BRUSHES, PALETTE, PIXEL_SIZES, PRO_BRUSHES, type DrawTool } from '../../drawing/DrawingCanvas';
import { GRID, isBlank, validateDrawing } from '../../drawing/strokes';
import { STAMPS, stampIcon } from '../../drawing/stamps';
import { cosmeticDef } from '../data/cosmetics';
import { canCreateHero, heroNameProblem } from '../systems/heroSlots';
import { icon } from '../../ui/icons';
import { crayonTitle, deskProps, paperScenery } from '../../ui/notebookArt';
import { button, confirmBox, meter, modal, setMeter, thumb } from '../../ui/components';
import { h, setChildren } from '../../ui/dom';
import { shareMonster } from '../../networking/share';
import { t } from '../../i18n';
import { showDrawingRules } from '../../ui/drawingRules';
import { clearDraft, saveDraft, takeDraft } from '../../app/resume';
import { enterLandscape, isPhone, releaseOrientation } from '../../app/fullscreen';

type CreatorParams = { monsterId?: string; heroMode?: boolean } | void;

/** Brush picker: emoji and name of each brush look. */
const BRUSH_INFO: Record<BrushKind, [string, string]> = {
  pencil: ['✏️', 'Pencil'],
  marker: ['🖊️', 'Marker'],
  crayon: ['🖍️', 'Crayon'],
  spray: ['💨', 'Spray'],
  watercolor: ['🖌️', 'Watercolour'],
  calligraphy: ['✒️', 'Broad nib'],
  pixel: ['👾', 'Pixel'],
};

interface MonsterForm {
  name: string;
  hp: number;
  atk: number;
  def: number;
  luck: number;
  skill: SkillId;
  personality: PersonalityId;
  tier: MonsterDTO['tier'];
  mode: MonsterDTO['mode'];
  exp: number;
  coins: number;
}

/** SCREEN 04 — draw a monster (or your hero), tune its stats, test and share it. */
export class CreatorScene extends Scene<CreatorParams> {
  private canvas!: DrawingCanvas;
  private heroMode = false;
  private editingId: string | null = null;
  private form: MonsterForm = { name: '', hp: 40, atk: 7, def: 3, luck: 0, skill: 'none', personality: 'chaotic', tier: 'sketch', mode: 'public', exp: 0, coins: 0 };
  private heroForm = { name: '', classId: HERO_CLASSES[0]!.id };
  private formDirty = false;
  private preview: FighterView | null = null;
  private previewArt: ArtHandle | null = null;
  private previewAnchor!: HTMLElement;
  private previewTimer = 0;
  private budgetMeter!: HTMLElement;
  private budgetText!: HTMLElement;
  private errorsEl!: HTMLElement;
  private rewardHint!: HTMLElement;
  private undoBtn!: HTMLButtonElement;
  private redoBtn!: HTMLButtonElement;
  private toolButtons: HTMLButtonElement[] = [];
  private colorButtons: HTMLButtonElement[] = [];
  /** Base palette plus the gel pens bought in the stationery shop. */
  private colors: string[] = [];
  /** The palette entry last picked (RGB pens change canvas.color with every stroke). */
  private pickedColor = '';
  private sizeButtons: HTMLButtonElement[] = [];
  /** Brush sizes of the current drawing mode. */
  private brushes: readonly number[] = EASY_BRUSHES;
  /** Pencil, marker, crayon, spray, watercolour, broad nib, pixel. */
  private lookButtons: HTMLButtonElement[] = [];
  private brushName!: HTMLElement;
  private mirrorBtn!: HTMLButtonElement;
  /** Move tool helpers: shown while it is in hand / something is selected. */
  private selectAllBtn!: HTMLButtonElement;
  private deleteSelBtn!: HTMLButtonElement;
  private arrangeRow!: HTMLElement;
  private toolRail!: HTMLElement;
  private paper!: HTMLElement;
  private focusBtn!: HTMLButtonElement;
  private stampButtons: HTMLButtonElement[] = [];
  private previewInfo!: HTMLElement;
  private saved = false;

  constructor(app: App) {
    super(app, 'scene-creator page');
  }

  enter(params: CreatorParams): void {
    const app = this.app;
    this.heroMode = !!(params && params.heroMode);
    this.dom.classList.toggle('creator-hero-mode', this.heroMode);
    this.canvas = new DrawingCanvas();
    this.d.add(() => this.canvas.dispose());
    // No paper light overlay over the drawing page: a clean view, and cheaper to repaint while drawing.
    document.documentElement.classList.add('on-drawing-page');
    this.d.add(() => document.documentElement.classList.remove('on-drawing-page'));
    this.d.add(() => this.previewArt?.release());
    this.d.add(() => window.clearTimeout(this.previewTimer));

    const id = params && params.monsterId;
    if (this.heroMode) {
      const hero = id ? app.store.state.heroes.find((x) => x.id === id) : undefined;
      if (hero) {
        this.editingId = hero.id;
        this.heroForm = { name: hero.name, classId: hero.classId };
        this.canvas.load(hero.drawing);
      } else {
        try {
          const pending = JSON.parse(sessionStorage.getItem('pl:newhero') ?? 'null') as { name: string; classId: string } | null;
          if (pending) this.heroForm = { name: pending.name, classId: heroClass(pending.classId).id };
        } catch {
          /* ignore */
        }
      }
    } else if (id) {
      const m = app.store.state.monsters.find((x) => x.id === id);
      if (m) this.loadMonster(m);
    } else {
      this.form.exp = rewardCap(statPower(this.form)).exp;
      this.form.coins = rewardCap(statPower(this.form)).coins;
    }
    // A page drawn before a reload (the automatic update, say) comes back unsaved.
    const draft = takeDraft(this.heroMode, this.editingId ?? null);
    if (draft) {
      this.canvas.load(draft.drawing as Parameters<DrawingCanvas['load']>[0]);
      this.canvas.dirty = true;
    }
    this.d.add(() => clearDraft());

    this.canvas.on('change', () => {
      this.refreshToolState();
      this.schedulePreview();
      saveDraft({ heroMode: this.heroMode, id: this.editingId ?? null, drawing: this.canvas.getDrawing() });
    });
    this.canvas.on('limit', (msg) => app.toast(msg, 'info'));
    this.canvas.on('selection', () => this.refreshToolState());

    this.previewAnchor = h('div', { class: 'preview-anchor', 'aria-label': t('Animated preview') });
    this.previewInfo = h('div', { class: 'preview-info', 'aria-live': 'polite' });
    const props = deskProps();
    props.append(h('div', { class: 'desk-crayons' }, h('i'), h('i'), h('i')));
    this.dom.append(
      h('div', { class: 'spiral', 'aria-hidden': 'true' }),
      paperScenery('creator-doodles'), props,
      h('div', { class: 'creator-kids', 'aria-hidden': 'true' }, icon('kids')),
      h(
        'header',
        { class: 'creator-top' },
        button(t('← Back'), { variant: 'ghost', small: true, sfx: app, onClick: () => void app.scenes.go('notebook', { tab: this.heroMode ? 'hero' : 'monsters' }, 'page-back') }),
        crayonTitle(this.heroMode ? t('Draw Your Hero') : t('Create & Share')),
        h('p', { class: 'creator-ribbon' }, this.heroMode ? t('Draw yourself as a legend!') : t('Design monsters and challenge friends ☺')),
        // Phones: Save sits top right, opposite Back (the full save buttons are further down).
        button(`💾 ${t('Save')}`, { small: true, variant: 'primary', color: 'yellow', sfx: app, onClick: () => (this.heroMode ? this.saveHero() : void this.saveMonster()) }),
      ),
      h(
        'div',
        { class: 'creator-main' },
        (this.toolRail = this.buildTools()),
        this.buildCanvas(),
        h('div', { class: 'creator-side' }, this.heroMode ? this.buildHeroEditor() : this.buildMonsterEditor()),
        h('section', { class: 'preview-column' },
          h('h3', { class: 'tape-title' }, icon('crown'), t('Preview')),
          h('div', { class: 'preview-card paper-scrap taped' }, icon('crown', 'preview-crown'), this.previewAnchor, this.previewInfo),
          h('p', { class: 'creator-note', 'aria-hidden': 'true' }, t("Silly? Scary? Heroic? It's up to you!")),
        ),
      ),
      ...(this.heroMode ? [] : [h('div', { class: 'creator-bottom' }, this.buildShareActions(), this.buildFriends())]),
      h('p', { class: 'creator-margin-note sticky-note yellow', 'aria-hidden': 'true' }, t('Your monsters. Your stories. Share the fun!')),
      h('p', { class: 'creator-steps', 'aria-hidden': 'true' }, t('Draw · Customize · Battle · Share'), h('br'), t('→ New Legends!')),
    );

    this.d.listen(window, 'keydown', (e: Event) => {
      const ke = e as KeyboardEvent;
      if (ke.target instanceof HTMLInputElement || ke.target instanceof HTMLSelectElement || ke.target instanceof HTMLTextAreaElement) return;
      const mod = ke.ctrlKey || ke.metaKey;
      if (mod && ke.key.toLowerCase() === 'z') {
        ke.preventDefault();
        if (ke.shiftKey) this.canvas.redo();
        else this.canvas.undo();
      } else if (mod && ke.key.toLowerCase() === 'y') {
        ke.preventDefault();
        this.canvas.redo();
      } else if (ke.key === 'e') this.setTool('eraser');
      else if (ke.key === 'b' || ke.key === 'p') this.setTool('pencil');
      else if (ke.key === 'f') this.setTool('fill');
      else if (ke.key === 'v') this.setTool('move');
      else if (ke.key === 'm') this.toggleMirror();
      else if ((ke.key === 'Delete' || ke.key === 'Backspace') && this.canvas.selectedCount > 0) {
        ke.preventDefault();
        this.canvas.deleteSelection();
      }
    });
    this.refreshToolState();
    this.refreshBudget();
    this.updatePreview();
  }

  private loadMonster(m: Monster): void {
    this.editingId = m.id;
    this.form = { name: m.name, hp: m.hp, atk: m.atk, def: m.def, luck: m.luck, skill: m.skill, personality: m.personality, tier: m.tier, mode: m.mode, exp: m.reward.exp, coins: m.reward.coins };
    this.canvas.load(m.drawing);
  }

  /* ---------------------------- tools ---------------------------- */

  private buildCanvas(): HTMLElement {
    const owned = this.app.store.state.cosmetics.owned;
    // Won stamps first after the free ones; stamps still to win stay greyed at the end of the strip.
    const stamps = [...STAMPS.filter((s) => !s.locked || owned.includes(`brush:${s.id}`)), ...STAMPS.filter((s) => s.locked && !owned.includes(`brush:${s.id}`))];
    this.stampButtons = stamps.map((s) => {
      const locked = !!s.locked && !owned.includes(`brush:${s.id}`);
      return h('button', {
        class: `stamp-btn ${locked ? 'locked' : ''}`, type: 'button', title: locked ? t('{name}: win it in the Paper Fortune Teller', { name: t(s.label) }) : t(s.label),
        'aria-label': locked ? t('Locked stamp: {name}', { name: t(s.label) }) : t('Stamp: {name}', { name: t(s.label) }),
        'aria-pressed': 'false', dataset: { stamp: s.id },
        on: { click: () => {
          if (locked) return this.app.toast(t('{name} is a prize of the Paper Fortune Teller (Notebook → Gear), or comes in a Special Shelf set.', { name: t(s.label) }), 'info');
          this.canvas.stamp = s.id; this.setTool('stamp');
        } },
      }, stampIcon(s.id), locked ? h('span', { class: 'stamp-lock', 'aria-hidden': 'true' }, '🔒') : null);
    });
    const strip = h('div', { class: 'stamp-scroll' }, this.stampButtons);
    const scrollStamps = (dir: 1 | -1): void => strip.scrollBy({ left: dir * strip.clientWidth * 0.7, behavior: 'smooth' });
    const prev = h('button', { type: 'button', class: 'stamp-prev', 'aria-label': t('Previous stamps'), on: { click: () => scrollStamps(-1) } }, '◀');
    const next = h('button', { type: 'button', class: 'stamp-next', 'aria-label': t('More stamps'), on: { click: () => scrollStamps(1) } }, '▶');
    // Arrows fade out at the ends of the strip.
    const ends = (): void => {
      prev.disabled = strip.scrollLeft <= 2;
      next.disabled = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 2;
    };
    strip.addEventListener('scroll', ends, { passive: true });
    requestAnimationFrame(ends);
    this.focusBtn = h('button', { type: 'button', class: 'canvas-focus-btn', on: { click: () => this.toggleFocus() } }, '') as HTMLButtonElement;
    this.renderFocusBtn();
    this.paper = h('div', { class: 'canvas-paper' }, this.canvas.el, this.focusBtn, h('span', { class: 'draw-here', 'aria-hidden': 'true' }, this.heroMode ? t('draw here ✎ (facing right →)') : t('draw here ✎ (facing right →, it turns to face the hero)')));
    return h('div', { class: 'canvas-wrap' },
      this.paper,
      h('div', { class: 'tool-group palette', role: 'group', 'aria-label': t('Colour palette') }, this.colorButtons),
      h('div', { class: 'stamps-row' }, h('b', null, t('Stamps')), prev, strip, next),
      h('p', { class: 'stamp-tip sticky-note pink', 'aria-hidden': 'true' }, icon('smile'), t('Use stamps to add cool parts!')),
    );
  }

  private buildTools(): HTMLElement {
    const app = this.app;
    const pro = app.store.state.settings.drawMode === 'pro';
    this.toolButtons = [];
    const EMOJI: Partial<Record<DrawTool, string>> = { fill: '🪣', move: '✋' };
    const toolBtn = (tool: DrawTool, label: string, key: string): HTMLButtonElement => {
      const e = EMOJI[tool];
      const b = h('button', { class: `tool-btn tool-${tool} paper-scrap`, type: 'button', title: key ? t('{tool} ({key})', { tool: label, key }) : label, 'aria-label': label, 'aria-pressed': 'false', on: { click: () => this.setTool(tool) } }, e ? h('span', { class: 'tool-emoji', 'aria-hidden': 'true' }, e) : icon(tool), h('span', null, label));
      this.toolButtons.push(b);
      return b;
    };
    // Colour buttons live under the canvas; made once, kept across mode switches.
    if (!this.colorButtons.length) {
      this.colors = [...PALETTE, ...ownedPens(app.store.state)];
      this.colorButtons = this.colors.map((c, i) => {
        const rgb = c.startsWith('cycle:') ? cosmeticDef(c.slice(6)) : undefined;
        const style = rgb?.cycle ? { '--c': rgb.cycle[0], background: `conic-gradient(${rgb.cycle.join(', ')}, ${rgb.cycle[0]})` } : { '--c': c };
        const label = rgb ? t(rgb.name) : c;
        return h('button', { class: `color-btn ${i >= PALETTE.length ? 'gel-pen' : ''} ${rgb ? 'rgb-pen' : ''}`, type: 'button', style: style as unknown as Partial<CSSStyleDeclaration>, title: label, 'aria-label': t('Colour {c}', { c: label }), on: { click: () => this.setColor(c) } });
      });
    }
    const pixel = this.canvas.brush === 'pixel';
    this.brushes = pixel ? PIXEL_SIZES : pro ? PRO_BRUSHES : EASY_BRUSHES;
    if (!this.brushes.includes(this.canvas.size)) this.canvas.size = this.brushes[1]!;
    this.sizeButtons = this.brushes.map((s) =>
      pixel
        ? h('button', { class: 'size-btn size-pixel', type: 'button', 'aria-label': t('Pixel picture {n}×{n}', { n: GRID / s }), title: t('Pixel picture {n}×{n}', { n: GRID / s }), on: { click: () => this.setSize(s) } }, `${GRID / s}`)
        : h('button', { class: 'size-btn', type: 'button', 'aria-label': t('Brush size {s}', { s }), on: { click: () => this.setSize(s) } }, h('span', { class: 'size-dot', style: { width: `${4 + Math.min(s, 24)}px`, height: `${4 + Math.min(s, 24)}px` } })),
    );
    this.lookButtons = BRUSH_LOOKS.map((k) => {
      const [emoji, label] = BRUSH_INFO[k];
      return h('button', { class: `look-btn look-${k}`, type: 'button', title: t(label), 'aria-label': t(label), 'aria-pressed': 'false', dataset: { look: k }, on: { click: () => this.setBrush(k) } },
        h('span', { class: 'tool-emoji', 'aria-hidden': 'true' }, emoji), h('span', null, t(label)));
    });
    this.undoBtn = button(h('span', { class: 'btn-with-icon' }, icon('undo'), t('Undo')), { small: true, sfx: app, onClick: () => this.canvas.undo(), title: t('Undo (Ctrl+Z)') });
    this.redoBtn = button(t('↷ Redo'), { small: true, sfx: app, onClick: () => this.canvas.redo(), title: t('Redo (Ctrl+Shift+Z)') });
    this.mirrorBtn = button(h('span', { class: 'btn-with-icon' }, h('span', { class: 'tool-emoji', 'aria-hidden': 'true' }, '🦋'), t('Mirror')), {
      small: true,
      sfx: app,
      title: t('Mirror (M): draw one side, the other side copies it'),
      onClick: () => this.toggleMirror(),
    });
    const mode = (m: 'easy' | 'pro', label: string): HTMLButtonElement => {
      const b = h('button', { class: `mode-btn ${(m === 'pro') === pro ? 'active' : ''}`, type: 'button', 'aria-pressed': String((m === 'pro') === pro), on: { click: () => this.setDrawMode(m) } }, label);
      return b;
    };
    this.selectAllBtn = button(t('Select all'), { small: true, sfx: app, onClick: () => this.canvas.selectAll() });
    this.deleteSelBtn = button(t('Delete selected'), { small: true, variant: 'danger', sfx: app, onClick: () => this.canvas.deleteSelection() });
    // Layer order of the selected pieces.
    const order = (how: 'front' | 'up' | 'down' | 'back', sym: string, label: string): HTMLButtonElement => {
      const b = button(sym, { small: true, sfx: app, title: label, onClick: () => this.canvas.arrange(how) });
      b.setAttribute('aria-label', label);
      return b;
    };
    this.arrangeRow = h(
      'div',
      { class: 'arrange-row', role: 'group', 'aria-label': t('Layer order') },
      h('span', { class: 'arrange-label' }, t('Order')),
      order('front', '⏫', t('Bring to front')),
      order('up', '🔼', t('Forward one')),
      order('down', '🔽', t('Back one')),
      order('back', '⏬', t('Send to back')),
    );
    const picker = h('input', { type: 'color', class: 'color-picker', value: this.canvas.color, 'aria-label': t('Any colour') }) as HTMLInputElement;
    picker.addEventListener('input', () => this.setColor(picker.value));
    return h(
      'div',
      { class: `tool-rail pencil-case paper-scrap draw-${pro ? 'pro' : 'easy'}`, role: 'toolbar', 'aria-label': t('Drawing tools') },
      h('h3', { class: 'tape-title' }, t('Tools')),
      h('div', { class: 'mode-switch', role: 'group', 'aria-label': t('Drawing mode'), title: pro ? t('Fine brushes and any colour you like.') : t('Big brushes: draw an outline, then tap the bucket inside it.') }, mode('easy', t('Easy')), mode('pro', t('Pro'))),
      h('p', { class: 'hand-hint mode-hint' }, pro ? t('Fine brushes and any colour you like.') : t('Big brushes: draw an outline, then tap the bucket inside it.')),
      h('div', { class: 'tool-group' }, toolBtn('pencil', t('Pencil'), 'P'), toolBtn('fill', t('Bucket'), 'F'), toolBtn('eraser', t('Eraser'), 'E'),
        toolBtn('stamp', t('Stamp'), ''), toolBtn('move', t('Move'), 'V'), this.mirrorBtn, this.undoBtn),
      h('div', { class: 'move-actions', title: t('Press a part and drag it, or drag a box around several parts first.') }, h('p', { class: 'hand-hint move-hint' }, t('Press a part and drag it, or drag a box around several parts first.')), this.selectAllBtn, this.deleteSelBtn, this.arrangeRow),
      h('div', { class: 'tool-group brush-looks', role: 'group', 'aria-label': t('Brushes') }, this.lookButtons),
      (this.brushName = h('p', { class: 'hand-hint brush-name', 'aria-live': 'polite' })),
      pixel ? h('p', { class: 'hand-hint pixel-hint' }, t('Pixel art: every dab fills one square. Pick how many squares the picture has:')) : null,
      h('div', { class: 'tool-group sizes' }, this.sizeButtons),
      pro ? h('label', { class: 'color-picker-row' }, picker, h('span', null, t('Any colour'))) : null,
      h(
        'details',
        { class: 'more-tools' },
        h('summary', null, t('More tools')),
        this.redoBtn,
        button(t('Clear'), {
          small: true,
          variant: 'danger',
          sfx: app,
          onClick: async () => {
            if (await confirmBox(t('Clear the page?'), t('Rub out the whole drawing? (You can still undo.)'), t('Clear'))) {
              this.canvas.clearAll();
              app.sfx.play('erase');
            }
          },
        }),
        this.heroMode ? null : button(t('Load…'), { small: true, variant: 'ghost', sfx: app, onClick: () => void this.openLoad() }),
      ),
      button(t('Drawing rules'), { small: true, variant: 'ghost', sfx: app, onClick: () => void showDrawingRules() }),
    );
  }

  /** Big drawing page: settings and preview step aside until toggled back. */
  private toggleFocus(): void {
    const on = this.dom.classList.toggle('drawing-focus');
    if (isPhone()) {
      if (on) {
        void enterLandscape().then((turned) => {
          if (!turned && innerHeight > innerWidth) this.app.toast(t('Turn your phone sideways for the biggest page.'), 'info');
        });
      } else releaseOrientation();
    }
    this.app.sfx.play('flip');
    this.renderFocusBtn();
    this.layout(this.app.viewport.info);
  }

  private renderFocusBtn(): void {
    const on = this.dom.classList.contains('drawing-focus');
    this.focusBtn.textContent = on ? `⤡ ${t('Smaller page')}` : `⛶ ${t('Bigger page')}`;
    this.focusBtn.setAttribute('aria-pressed', String(on));
  }

  private setTool(tool: DrawTool): void {
    this.canvas.tool = tool;
    this.app.sfx.play(tool === 'eraser' ? 'erase' : 'scribble');
    this.refreshToolState();
  }

  private toggleMirror(): void {
    this.canvas.mirror = !this.canvas.mirror;
    this.app.sfx.play('flip');
    this.refreshToolState();
  }

  /** Easy or pro tools; remembered in the settings. */
  private setDrawMode(m: 'easy' | 'pro'): void {
    if (this.app.store.state.settings.drawMode === m) return;
    this.app.store.update(['settings'], (s) => (s.settings.drawMode = m));
    const next = this.buildTools();
    this.toolRail.replaceWith(next);
    this.toolRail = next;
    this.app.sfx.play('flip');
    this.refreshToolState();
  }
  private setColor(c: string): void {
    const rgb = c.startsWith('cycle:') ? cosmeticDef(c.slice(6))?.cycle : undefined;
    this.canvas.cycle = rgb ?? null;
    this.canvas.color = rgb ? rgb[0]! : c;
    this.pickedColor = c;
    // Picking a colour keeps the stamp or bucket in hand; from the eraser it goes back to the pencil.
    if (this.canvas.tool === 'eraser') this.canvas.tool = 'pencil';
    this.refreshToolState();
  }
  private setSize(s: number): void {
    this.canvas.size = s;
    this.canvas.repaint();
    this.refreshToolState();
  }

  /** Brush look; switching in or out of pixel art swaps the size buttons. */
  private setBrush(k: BrushKind): void {
    const wasPixel = this.canvas.brush === 'pixel';
    this.canvas.brush = k;
    if (this.canvas.tool !== 'pencil' && !(k === 'pixel' && (this.canvas.tool === 'eraser' || this.canvas.tool === 'fill'))) this.canvas.tool = 'pencil';
    if (wasPixel !== (k === 'pixel')) {
      const next = this.buildTools();
      this.toolRail.replaceWith(next);
      this.toolRail = next;
    }
    this.canvas.repaint();
    this.app.sfx.play('scribble');
    this.refreshToolState();
  }

  private refreshToolState(): void {
    const active = (b: HTMLButtonElement, on: boolean): void => { b.classList.toggle('active', on); b.setAttribute('aria-pressed', String(on)); };
    this.toolButtons.forEach((b) => active(b, b.classList.contains(`tool-${this.canvas.tool}`)));
    this.colorButtons.forEach((b, i) => active(b, this.colors[i] === (this.canvas.cycle ? this.pickedColor : this.canvas.color)));
    this.sizeButtons.forEach((b, i) => active(b, this.brushes[i] === this.canvas.size));
    this.lookButtons.forEach((b) => active(b, b.dataset.look === this.canvas.brush));
    this.brushName.textContent = `${t('Brushes')}: ${t(BRUSH_INFO[this.canvas.brush][1])}`;
    active(this.mirrorBtn, this.canvas.mirror);
    const moving = this.canvas.tool === 'move';
    this.selectAllBtn.closest('.move-actions')?.toggleAttribute('hidden', !moving);
    this.deleteSelBtn.hidden = this.canvas.selectedCount === 0;
    this.arrangeRow.hidden = this.canvas.selectedCount === 0;
    this.paper.classList.toggle('mirror-on', this.canvas.mirror);
    this.stampButtons.forEach((b) => active(b, b.dataset.stamp === this.canvas.stamp && this.canvas.tool === 'stamp'));
    this.undoBtn.disabled = !this.canvas.canUndo;
    this.redoBtn.disabled = !this.canvas.canRedo;
    if (this.toolRail?.isConnected && Math.round(this.toolRail.getBoundingClientRect().height) !== Math.round(this.fittedRail)) this.fitPage();
  }

  private async openLoad(): Promise<void> {
    const mine = this.app.store.state.monsters.filter((m) => m.source === 'mine');
    if (!mine.length) {
      this.app.toast(t('No saved monsters yet — draw one and press Save!'), 'info');
      return;
    }
    const list = h(
      'div',
      { class: 'load-list' },
      mine.map((m) =>
        h('button', { class: 'doodle-card small', type: 'button', dataset: { id: m.id } }, thumb(this.app, m.drawing, 'monster:gum_blob', 56), h('b', null, m.name)),
      ),
    );
    let picked: string | null = null;
    list.addEventListener('click', (e) => {
      const card = (e.target as HTMLElement).closest<HTMLElement>('[data-id]');
      if (card) {
        picked = card.dataset.id ?? null;
        (list.closest('.modal-card')?.querySelector('.sketch-btn.primary') as HTMLButtonElement | null)?.click();
      }
    });
    const v = await modal(t('Load a monster'), list, [
      { label: t('Cancel'), value: 'no', variant: 'ghost' },
      { label: t('Load'), value: 'ok', variant: 'primary', color: 'yellow' },
    ]);
    const m = mine.find((x) => x.id === picked);
    if (v === 'ok' && m) {
      if (this.canvas.dirty && !(await confirmBox(t('Replace drawing?'), t('Your unsaved drawing will be replaced.'), t('Load')))) return;
      this.loadMonster(m);
      setChildren(this.dom.querySelector('.creator-side')!, this.buildMonsterEditor());
      this.refreshBudget();
      this.refreshToolState();
      this.updatePreview();
    }
  }

  /* ------------------------- monster editor ------------------------- */

  private buildMonsterEditor(): HTMLElement {
    const app = this.app;
    const f = this.form;
    const r = RULES.stats;
    const touch = (): void => {
      this.formDirty = true;
      this.refreshBudget();
      this.refreshPreviewInfo();
    };
    const statRow = (key: 'hp' | 'atk' | 'def' | 'luck', label: string, range: { min: number; max: number }): HTMLElement => {
      const num = h('input', { type: 'number', class: 'hand-input num', min: String(range.min), max: String(range.max), value: String(f[key]), 'aria-label': label });
      const slider = h('input', { type: 'range', class: 'hand-range', min: String(range.min), max: String(range.max), value: String(f[key]), 'aria-label': t('{stat} slider', { stat: label }) });
      const paintSlider = (): void => slider.style.setProperty('--stat-progress', `${100 * (f[key] - range.min) / (range.max - range.min)}%`);
      paintSlider();
      const set = (v: number): void => {
        f[key] = Math.round(Math.min(range.max, Math.max(range.min, Number.isFinite(v) ? v : range.min)));
        num.value = String(f[key]);
        slider.value = String(f[key]);
        paintSlider();
        touch();
      };
      slider.addEventListener('input', () => set(Number(slider.value)));
      num.addEventListener('change', () => set(Number(num.value)));
      const arrow = (delta: number): HTMLButtonElement => h('button', { type: 'button', class: 'stat-step', 'aria-label': t('{stat} {change}', { stat: label, change: delta < 0 ? '−1' : '+1' }), on: { click: () => set(f[key] + delta) } }, delta < 0 ? '◀' : '▶');
      return h('div', { class: `stat-edit stat-edit-${key}` }, h('span', { class: 'stat-name' }, icon({ hp: 'heart', atk: 'sword', def: 'shield', luck: 'clover' }[key]), label), num, arrow(-1), slider, arrow(1));
    };
    const nameInput = h('input', { class: 'hand-input big', maxLength: 24, value: f.name, placeholder: t('Monster name'), 'aria-label': t('Monster name') });
    nameInput.addEventListener('input', () => {
      f.name = nameInput.value;
      this.formDirty = true;
      this.refreshPreviewInfo();
    });
    const sel = <T extends string>(label: string, value: T, opts: [T, string][], on: (v: T) => void): HTMLElement => {
      const s = h('select', { class: 'hand-select', 'aria-label': label }, opts.map(([v, text]) => h('option', { value: v, selected: v === value }, text)));
      const description = (): string => label === t('Skill') ? t(skillDef(f.skill).description) : label === t('Personality') ? t(RULES.personalities.find((p) => p.id === f.personality)!.description) : '';
      const hint = h('small', { class: 'select-description' }, description());
      s.addEventListener('change', () => {
        on(s.value as T);
        hint.textContent = description();
        touch();
      });
      return h('label', { class: 'field field-choice' }, h('span', null, label === t('Skill') ? icon('star') : label === t('Personality') ? icon('slime') : null, label), s, hint);
    };
    const expIn = h('input', { type: 'number', class: 'hand-input num', min: '0', value: String(f.exp), 'aria-label': t('EXP reward') });
    const coinIn = h('input', { type: 'number', class: 'hand-input num', min: '0', value: String(f.coins), 'aria-label': t('Coin reward') });
    expIn.addEventListener('change', () => ((f.exp = Math.max(0, Math.round(Number(expIn.value) || 0))), touch()));
    coinIn.addEventListener('change', () => ((f.coins = Math.max(0, Math.round(Number(coinIn.value) || 0))), touch()));
    this.rewardHint = h('small', { class: 'hand-hint' });
    this.budgetMeter = meter(0, 100, 'budget');
    this.budgetText = h('span', { class: 'budget-text' });
    this.errorsEl = h('ul', { class: 'form-errors', 'aria-live': 'polite' });

    const modeToggle = h(
      'div',
      { class: 'mode-toggle', role: 'radiogroup', 'aria-label': t('Rules mode') },
      (['public', 'classroom'] as const).map((m) => {
        const b = h('button', { type: 'button', role: 'radio', class: `mode-btn ${f.mode === m ? 'active' : ''}`, 'aria-checked': String(f.mode === m) }, m === 'public' ? t('Public (balanced)') : t('Classroom (free stats)'));
        b.addEventListener('click', () => {
          f.mode = m;
          modeToggle.querySelectorAll('.mode-btn').forEach((x) => {
            x.classList.toggle('active', x === b);
            x.setAttribute('aria-checked', String(x === b));
          });
          touch();
        });
        return b;
      }),
    );

    const rewardSelect = h('select', { class: 'hand-select', 'aria-label': t('Reward') },
      h('option', { value: 'custom' }, t('Custom rewards')),
      h('option', { value: 'both' }, t('EXP & coins')),
      h('option', { value: 'exp' }, t('EXP only')),
      h('option', { value: 'coins' }, t('Coins only')),
      h('option', { value: 'none' }, t('No rewards')),
    );
    const rewardDescription = h('small', { class: 'select-description reward-description' }, t('{exp} EXP · {coins} coins', { exp: f.exp, coins: f.coins }));
    rewardSelect.addEventListener('change', () => {
      if (rewardSelect.value === 'custom') return;
      const cap = rewardCap(statPower(f));
      f.exp = ['both', 'exp'].includes(rewardSelect.value) ? cap.exp : 0;
      f.coins = ['both', 'coins'].includes(rewardSelect.value) ? cap.coins : 0;
      expIn.value = String(f.exp); coinIn.value = String(f.coins);
      touch();
    });
    const names = (): string[] => [t('Sir Scribbles'), t('Captain Crumbs'), t('Wobbly Waffle'), t('Professor Puddle'), t('Doodle Dragon'), t('Crayon Goblin'), t('Sleepy Spark'), t('King Noodle')];
    const dice = button(icon('dice'), { small: true, title: t('Random monster name'), onClick: () => {
      const options = names(); f.name = options[Math.floor(Math.random() * options.length)]!;
      nameInput.value = f.name; touch();
    } });
    dice.setAttribute('aria-label', t('Random monster name'));

    return h(
      'div',
      { class: 'stats-editor paper-scrap' },
      h('h3', { class: 'tape-title' }, icon('star'), t('Monster Settings')),
      h('div', { class: 'name-row' }, h('label', { class: 'field' }, h('span', null, icon('bat'), t('Name')), nameInput), dice),
      statRow('hp', 'HP', r.hp),
      statRow('atk', 'ATK', r.atk),
      statRow('def', 'DEF', r.def),
      sel(t('Skill'), f.skill, RULES.skills.map((s) => [s.id, t('{skill} ({cost} pts)', { skill: t(s.name), cost: s.cost })] as [SkillId, string]), (v) => (f.skill = v)),
      sel(t('Personality'), f.personality, RULES.personalities.map((p) => [p.id, t(p.name)] as [PersonalityId, string]), (v) => (f.personality = v)),
      h('label', { class: 'field field-choice' }, h('span', null, icon('chest'), t('Reward')), rewardSelect, rewardDescription),
      h('details', { class: 'creator-advanced' }, h('summary', null, t('Advanced')),
        modeToggle,
        h('p', { class: 'hand-hint mode-note' }),
        sel(t('Tier'), f.tier, RULES.budget.tiers.map((x) => [x.id, t('{tier} (budget {budget})', { tier: t(x.name), budget: x.budget })] as [MonsterDTO['tier'], string]), (v) => (f.tier = v)),
        h('div', { class: 'budget-row' }, h('span', null, t('Power')), this.budgetMeter, this.budgetText),
        statRow('luck', 'LUCK', r.luck),
      h(
        'div',
        { class: 'reward-edit' },
        h('span', { class: 'stat-name' }, t('Reward')),
        h('label', null, 'EXP ', expIn),
        h('label', null, t('Coins') + ' ', coinIn),
        button(t('Max'), {
          small: true,
          variant: 'ghost',
          sfx: app,
          onClick: () => {
            const cap = rewardCap(statPower(f));
            f.exp = cap.exp;
            f.coins = cap.coins;
            expIn.value = String(cap.exp);
            coinIn.value = String(cap.coins);
            touch();
          },
        }),
      ),
      this.rewardHint),
      this.errorsEl,
    );
  }

  private toDTO(): MonsterDTO {
    const f = this.form;
    return { name: f.name.trim(), hp: f.hp, atk: f.atk, def: f.def, luck: f.luck, skill: f.skill, personality: f.personality, reward: { exp: f.exp, coins: f.coins }, mode: f.mode, tier: f.tier, drawing: this.canvas.getDrawing() };
  }

  private refreshBudget(): void {
    if (this.heroMode || !this.budgetMeter) return;
    const f = this.form;
    const power = statPower(f);
    const budget = tierDef(f.tier).budget;
    const free = f.mode === 'classroom';
    setMeter(this.budgetMeter, power, budget);
    this.budgetMeter.classList.toggle('over', !free && power > budget);
    this.budgetText.textContent = free ? t('{power} (no limit)', { power }) : `${power} / ${budget}`;
    const cap = rewardCap(power);
    const rewardDescription = this.dom.querySelector('.reward-description');
    if (rewardDescription) rewardDescription.textContent = t('{exp} EXP · {coins} coins', { exp: f.exp, coins: f.coins });
    this.rewardHint.textContent = free ? t('Classroom monsters are practice-only: they give no EXP or coins in your notebook.') : t('Max reward for this power: {exp} EXP, {coins} coins.', { exp: cap.exp, coins: cap.coins });
    const note = this.dom.querySelector('.mode-note');
    if (note) note.textContent = free ? t('Classroom: any stats within range, for custom class rules. Never ranked.') : t('Public: stats must fit the {tier} budget so battles stay fair.', { tier: t(tierDef(f.tier).name) });
    const v = validateMonster({ ...this.toDTO(), name: f.name.trim() || 'x' });
    setChildren(this.errorsEl, v.errors.map((e) => h('li', null, e)));
  }

  private validateForSave(): MonsterDTO | null {
    const dto = this.toDTO();
    if (isBlank(dto.drawing)) {
      this.app.toast(t('Draw your monster first! ✎'), 'info');
      return null;
    }
    const dErr = validateDrawing(dto.drawing!);
    if (dErr) {
      this.app.toast(dErr, 'error');
      return null;
    }
    if (!dto.name) {
      this.app.toast(t('Give your monster a name!'), 'info');
      return null;
    }
    const v = validateMonster(dto);
    if (!v.ok) {
      this.app.toast(v.errors[0] ?? t('Invalid monster'), 'error');
      return null;
    }
    return dto;
  }

  private async saveMonster(): Promise<Monster | null> {
    const dto = this.validateForSave();
    if (!dto) return null;
    const existing = this.editingId ? this.app.store.state.monsters.find((m) => m.id === this.editingId) : undefined;
    const monster: Monster = existing
      ? { ...existing, ...dto, reward: dto.reward, drawing: dto.drawing }
      : { ...dto, id: newId('m'), source: 'mine', wins: 0, losses: 0, createdAt: Date.now() };
    this.app.store.update(['monsters', 'quests', 'memories'], (s) => {
      if (existing) {
        const i = s.monsters.findIndex((m) => m.id === existing.id);
        if (i >= 0) s.monsters[i] = monster;
      } else {
        s.monsters.push(monster);
        s.counters.monstersCreated++;
        addMemory(s, { kind: 'create', title: t('Drew {name}', { name: monster.name }), text: t('A {tier}-tier monster with {skill}.', { tier: t(tierDef(monster.tier).name), skill: t(skillDef(monster.skill).name) }), sticker: 'pencil' });
      }
    });
    this.editingId = monster.id;
    this.canvas.dirty = false;
    this.formDirty = false;
    this.saved = true;
    this.app.sfx.play('scribble');
    this.app.toast(t('{name} saved to your notebook!', { name: monster.name }), 'success');
    return monster;
  }

  private async testBattle(): Promise<void> {
    if (!this.app.store.hero) {
      this.app.toast(t('You need a hero to test-battle. Create one in the notebook!'), 'info');
      return;
    }
    const m = await this.saveMonster();
    if (m) void this.app.scenes.go('battle', { mode: 'pve', monsterId: m.id }, 'page');
  }

  private async share(): Promise<void> {
    const m = await this.saveMonster();
    if (m) await shareMonster(this.app, m);
  }

  private buildShareActions(): HTMLElement {
    const action = (label: string, caption: string, name: string, color: 'green' | 'yellow' | 'pink' | 'blue', onClick: () => void): HTMLElement => {
      const b = button(h('span', { class: 'share-button-label' }, icon(name), h('b', null, label)), { color, sfx: this.app, onClick });
      b.setAttribute('aria-label', label);
      return h('div', { class: 'share-action' }, b, h('p', null, caption));
    };
    return h('section', { class: 'share-strip' },
      h('h3', { class: 'tape-title tape-title--blue' }, icon('plane'), t('Share & Challenge')),
      h('div', { class: 'share-action-row' },
        action(t('Test Battle'), t('Try your monster against AI!'), 'swords', 'green', () => void this.testBattle()),
        action(t('Save to Notebook'), t('Keep it in your collection!'), 'book', 'yellow', () => void this.saveMonster()),
        action(t('Share with Friends'), t('Let your friends draw and battle it!'), 'kids', 'pink', () => void this.share()),
        action(t('Create Challenge Room'), t('Set up a room and invite friends!'), 'plane', 'blue', () => void this.challenge()),
      ),
    );
  }

  private async challenge(friend?: { id: string; name: string }): Promise<void> {
    const monster = await this.saveMonster();
    if (monster) void this.app.scenes.go('multiplayer', { monsterId: monster.id, ...(friend ? { inviteFriend: friend } : {}) }, 'page');
  }

  private buildFriends(): HTMLElement {
    const app = this.app;
    let friends: FriendDTO[] = [];
    let recent = false;
    const list = h('div', { class: 'creator-friend-list', role: 'tabpanel', id: 'creator-friends-panel' });
    const tabs: HTMLButtonElement[] = [];
    const empty = (message: string): HTMLElement => h('p', { class: 'friends-empty' }, icon('kids'), message);
    const render = (): void => {
      tabs.forEach((b, i) => { const selected = (i === 1) === recent; b.classList.toggle('active', selected); b.setAttribute('aria-selected', String(selected)); });
      if (recent) {
        const rivals = [...app.store.state.friends].filter((f) => f.battles > 0).sort((a, b) => b.lastSeen - a.lastSeen).slice(0, 3);
        setChildren(list, rivals.length ? rivals.map((f) => h('div', { class: 'creator-friend' }, icon('crown', 'friend-avatar'), h('div', null, h('b', null, f.name), h('small', null, t('{wins}W / {losses}L vs you', { wins: f.wins, losses: f.losses }))))) : empty(t('Your next challenge starts a new story!')));
        return;
      }
      const connected = app.mp.state === 'online';
      if (!connected) { setChildren(list, empty(t('Connect in Friends to find your drawing buddies.'))); return; }
      const rows = [...friends].sort((a, b) => Number(b.online) - Number(a.online)).map((f) => h('div', { class: 'creator-friend' },
        icon('crown', 'friend-avatar'),
        h('div', null, h('b', null, f.name), h('small', { class: f.online ? 'friend-is-online' : '' }, f.online ? t('online now') : t('offline'))),
        button(t('Challenge me!'), { small: true, color: 'pink', disabled: !f.online, onClick: () => void this.challenge({ id: f.id, name: f.name }) }),
      ));
      setChildren(list, rows.length ? rows : empty(t('Add a friend in your notebook and share the fun!')));
    };
    const load = async (): Promise<void> => {
      if (app.mp.state !== 'online' || !app.store.state.online) { render(); return; }
      try {
        app.api.token = app.store.state.online.token;
        const result = await app.api.friends();
        if (this.isDestroyed) return;
        friends = result.friends;
        render();
      } catch { if (!this.isDestroyed) setChildren(list, empty(t('Friends are taking a pencil break. Try again in your notebook.'))); }
    };
    tabs.push(...[t('Friends'), t('Recent Challenges')].map((label, i) => h('button', { type: 'button', class: 'friend-tab', role: 'tab', 'aria-controls': 'creator-friends-panel', 'aria-selected': String(i === 0), on: { click: () => { recent = i === 1; render(); } } }, label)));
    this.d.add(app.mp.on('friend.online', (message) => { const f = friends.find((x) => x.id === message.id); if (f) f.online = message.online; render(); }));
    for (const event of ['friend.accepted', 'friend.removed', 'state'] as const) this.d.add(app.mp.on(event, () => void load()));
    void load();
    return h('section', { class: 'creator-friends paper-scrap' },
      h('h3', { class: 'tape-title' }, icon('kids'), t('Friends & Challenges')),
      h('button', { type: 'button', class: 'friends-refresh', 'aria-label': t('Refresh friends'), on: { click: () => void load() } }, '↻'),
      h('div', { class: 'friend-tabs', role: 'tablist', 'aria-label': t('Friends & Challenges') }, tabs), list,
      h('p', { class: 'friends-note', 'aria-hidden': 'true' }, t('Friends make stronger legends!')),
    );
  }

  /* -------------------------- hero editor -------------------------- */

  private buildHeroEditor(): HTMLElement {
    const app = this.app;
    const nameInput = h('input', { class: 'hand-input big', maxLength: 24, value: this.heroForm.name, placeholder: t('Hero name'), 'aria-label': t('Hero name') });
    nameInput.addEventListener('input', () => {
      this.heroForm.name = nameInput.value;
      this.formDirty = true;
      this.refreshPreviewInfo();
    });
    const cls = h('select', { class: 'hand-select', 'aria-label': t('Class') }, HERO_CLASSES.map((c) => h('option', { value: c.id, selected: c.id === this.heroForm.classId }, `${t(c.name)} — ★ ${t(skillDef(c.skill).name)}`)));
    cls.addEventListener('change', () => {
      this.heroForm.classId = cls.value;
      this.formDirty = true;
      this.updatePreview();
    });
    return h(
      'div',
      { class: 'stats-editor paper-card' },
      h('label', { class: 'field' }, h('span', null, t('Name')), nameInput),
      h('label', { class: 'field' }, h('span', null, t('Class (sets stats & skill)')), cls),
      h('p', { class: 'hand-hint' }, t('Leave the page empty to keep the class doodle.')),
      h(
        'div',
        { class: 'creator-actions' },
        button(this.editingId ? t('Save Hero') : t('Create Hero'), { variant: 'primary', color: 'yellow', sfx: app, onClick: () => this.saveHero() }),
      ),
    );
  }

  private saveHero(): void {
    const drawing = this.canvas.getDrawing();
    const err = isBlank(drawing) ? null : validateDrawing(drawing);
    if (err) return this.app.toast(err, 'error');
    const art = isBlank(drawing) ? null : drawing;
    // Every hero needs a name of its own (the class name does not count).
    const problem = heroNameProblem(this.heroForm.name, this.heroForm.classId);
    if (problem) return this.app.toast(problem, 'error');
    if (!this.editingId && !canCreateHero(this.app.store.state)) return this.app.toast(t('Your hero slots are full. Unlock another one in Notebook → New Hero.'), 'error');
    const name = this.heroForm.name.trim();
    if (this.editingId) {
      const id = this.editingId;
      this.app.store.update(['hero'], (s) => {
        const hh = s.heroes.find((x) => x.id === id);
        if (!hh) return;
        hh.name = name.slice(0, 24);
        // A fresh drawing is worn again instead of a ready-made look.
        if (art && this.canvas.dirty) hh.look = null;
        hh.drawing = art;
        if (hh.classId !== this.heroForm.classId) {
          hh.classId = this.heroForm.classId;
          hh.skill = heroClass(hh.classId).skill;
        }
      });
    } else {
      const hero = createHero(name, this.heroForm.classId, art);
      this.app.store.update(['hero', 'inventory', 'memories'], (s) => {
        s.heroes.push(hero);
        s.activeHeroId = hero.id;
        addMemory(s, { kind: 'create', title: t('{name} joined the notebook', { name: hero.name }), text: art ? t('Hand-drawn hero!') : t('A {heroClass}.', { heroClass: t(heroClass(hero.classId).name) }), sticker: 'pencil' });
      });
      sessionStorage.removeItem('pl:newhero');
    }
    this.canvas.dirty = false;
    this.formDirty = false;
    this.saved = true;
    this.app.toast(t('{name} is ready!', { name }), 'success');
    void this.app.scenes.go('notebook', { tab: 'hero' }, 'page-back');
  }

  /* ---------------------------- preview ---------------------------- */

  private schedulePreview(): void {
    window.clearTimeout(this.previewTimer);
    // Rebaking the animated preview redraws every stroke several times: wait for a pause in
    // drawing, then do it when the browser is idle so it never lands on the next stroke.
    this.previewTimer = window.setTimeout(() => {
      if (typeof requestIdleCallback === 'function') requestIdleCallback(() => this.updatePreview(), { timeout: 600 });
      else this.updatePreview();
    }, 650);
  }

  private updatePreview(): void {
    if (this.isDestroyed) return;
    const drawing = this.canvas.getDrawing();
    const fallback = this.heroMode ? `hero:${this.heroForm.classId}` : 'monster:gum_blob';
    const next = acquireArt(this.app, drawing, fallback);
    this.previewArt?.release();
    this.previewArt = next;
    if (!this.preview) {
      this.preview = new FighterView(next, this.heroMode ? 1 : -1, this.app.tweens);
      this.root.addChild(this.preview);
    } else this.preview.setArt(next);
    this.layout(this.app.viewport.info);
    this.refreshPreviewInfo();
  }

  private refreshPreviewInfo(): void {
    if (!this.previewInfo) return;
    const f = this.form;
    const cls = heroClass(this.heroForm.classId);
    const skill = skillDef(this.heroMode ? cls.skill : f.skill);
    setChildren(this.previewInfo,
      h('h3', { class: 'preview-name' }, this.heroMode ? this.heroForm.name || t(cls.name) : f.name || t('Your Monster')),
      h('p', { class: 'preview-level' }, t('Lv. {n}', { n: 1 }), ' · ', h('span', { class: 'preview-stars', 'aria-label': t('{n} of {max} stars', { n: 1, max: 3 }) }, '★☆☆')),
      this.heroMode ? null : h('div', { class: 'preview-stats' },
        ...([['heart', 'HP', f.hp], ['sword', 'ATK', f.atk], ['shield', 'DEF', f.def]] as const).map(([name, label, value]) => h('span', null, icon(name), h('small', null, label), h('b', null, String(value)))),
      ),
      h('b', { class: 'preview-skill' }, icon('star'), t(skill.name)), h('p', { class: 'preview-description' }, t(skill.description)),
    );
  }

  /** Height of the tools the last time the page was fitted (the move tool's extra row changes it). */
  private fittedRail = 0;

  /**
   * Portrait phones: the page shrinks just enough that the colours, stamps and
   * every tool under it fit on the first screen without scrolling (CSS --page-fit).
   */
  private fitPage(): void {
    if (!this.paper?.isConnected || !this.toolRail?.isConnected) return;
    if (this.dom.classList.contains('fixed-stage') || !matchMedia('(orientation: portrait)').matches) {
      this.dom.style.removeProperty('--page-fit');
      return;
    }
    const top = this.dom.getBoundingClientRect().top;
    const rail = this.toolRail.getBoundingClientRect();
    // Everything from the top of the scene to the bottom of the tools, except the page itself.
    const others = rail.bottom - top + this.dom.scrollTop - this.paper.getBoundingClientRect().height;
    const screen = window.visualViewport?.height ?? window.innerHeight;
    const width = this.paper.parentElement?.clientWidth ?? this.paper.clientWidth;
    const size = Math.floor(Math.max(Math.min(200, width), Math.min(width, screen - top - others - 6)));
    this.dom.style.setProperty('--page-fit', `${size}px`);
    this.fittedRail = rail.height;
  }

  override layout(_vp: ViewportInfo): void {
    this.fitPage();
    if (!this.preview || !this.previewAnchor?.isConnected) return;
    const a = this.anchor(this.previewAnchor);
    this.preview.visible = a.h > 10;
    this.preview.position.set(a.cx, a.y + a.h * 0.98);
    this.preview.setHeight(a.h * 0.95);
  }

  override update(dt: number): void {
    this.preview?.update(dt);
  }

  override shown(): void {
    const onScroll = (): void => this.layout(this.app.viewport.info);
    this.d.listen(this.dom, 'scroll', onScroll, { passive: true, capture: true });
    this.layout(this.app.viewport.info);
    // Web fonts can still change the height of the tool rows after the first layout.
    void document.fonts?.ready.then(() => this.fitPage());
  }

  override async canLeave(): Promise<boolean> {
    if (this.saved && !this.canvas.dirty && !this.formDirty) return true;
    if (!this.canvas.dirty && !this.formDirty) return true;
    return confirmBox(t('Leave without saving?'), t('Your drawing has unsaved pencil marks.'), t('Leave'), t('Stay'));
  }
}
